import { useCallback, useEffect, useRef, useState } from "react"
import { stickerColor } from "@/components/VoteToken/stickerColors"
import styles from "@/styles/components/VoteToken.module.css"

/*
 * Carrying a sticker from the tray onto a pain note — the drag `Exercise.jpg`
 * asks for, layered the same way `useBoardDrag` layers the Priorities board's
 * drag over its click-to-place: pointer events (one code path for mouse, pen
 * and touch), a hand-built ghost parked on `body` so it can cross the grid,
 * and everything a move or a release reads kept in a ref because a flick can
 * deliver its last `pointermove` and the `pointerup` in a single task.
 *
 * A press under `THRESHOLD` is not a drag at all — clicking a note to vote
 * still works exactly as it did, this only adds the gesture beside it.
 */
const THRESHOLD = 6

interface PainVoteDragOptions {
  /** Nothing to spend, the round is read back, or the vote is locked in. */
  enabled: boolean
  /** The colour index the sticker will render at once it lands on this note —
   *  the same vote-ordinal lookup `PainsBoard` uses for the note's own
   *  sticker. The ghost is recoloured to it on entry so what you are carrying
   *  over a note is already what it becomes on release, never a swap at
   *  drop. */
  colorIndexFor: (painId: string) => number
  /** Called once, on release over a note. Never for a press that did not move. */
  onDrop: (painId: string) => void
}

interface Press {
  index: number
  pointerId: number
  x: number
  y: number
  moving: boolean
  over: string | null
  ghost: HTMLElement | null
}

export interface PainVoteDrag {
  /** The tray index under the pointer, so its cell can dim where it came from. */
  carrying: number | null
  /** The pain id the sticker would land on. */
  over: string | null
  /** `onPointerDown` for a tray sticker. */
  grab: (index: number) => (event: React.PointerEvent) => void
}

export function usePainVoteDrag({
  enabled,
  colorIndexFor,
  onDrop,
}: PainVoteDragOptions): PainVoteDrag {
  const [carrying, setCarrying] = useState<number | null>(null)
  const [over, setOver] = useState<string | null>(null)

  const press = useRef<Press | null>(null)
  const detach = useRef<(() => void) | null>(null)
  /* Read at release, not at grab: a drop has to answer to the round as it is
     when the pointer comes up, not as it was when the sticker left the tray. */
  const latest = useRef({ enabled, colorIndexFor, onDrop })
  useEffect(() => {
    latest.current = { enabled, colorIndexFor, onDrop }
  })

  /* The half of ending a drag that is not React's. The listeners are on
     `window` and the ghost is parked on `body`, so nothing else takes either
     off. */
  const release = useCallback(() => {
    press.current?.ghost?.remove()
    press.current = null
    detach.current?.()
    detach.current = null
    document.body.style.userSelect = ""
  }, [])

  const end = useCallback(() => {
    release()
    setCarrying(null)
    setOver(null)
  }, [release])

  /* A drag still running when the phase moves on. Voting closing mid-drag is
     that case without an unmount to lean on: the board stays mounted from
     `vote` into `tally`, so the ghost would sit on `body` waiting for a
     release that is no longer allowed to vote. */
  useEffect(() => {
    if (!enabled) release()
  }, [enabled, release])
  useEffect(() => release, [release])

  // And the lighting the cancelled drag left on the tray and the note.
  if (!enabled && (carrying !== null || over !== null)) {
    setCarrying(null)
    setOver(null)
  }

  const grab = (index: number) => (event: React.PointerEvent) => {
    if (!enabled) return
    // A right or middle press is not a grab.
    if (event.button !== 0) return
    // Nor is a second finger, which arrives with `button === 0` like any
    // other touch — `isPrimary` is the only thing that tells the two apart.
    if (!event.isPrimary) return
    // A press left standing: the pointer was released outside the window,
    // where there is no `pointerup` to hear. Dropped rather than refused.
    if (press.current) end()

    press.current = {
      index,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      moving: false,
      over: null,
      ghost: null,
    }

    const move = (moveEvent: PointerEvent) => {
      const state = press.current
      if (!state || moveEvent.pointerId !== state.pointerId) return

      if (!state.moving) {
        if (Math.hypot(moveEvent.clientX - state.x, moveEvent.clientY - state.y) < THRESHOLD) {
          return
        }
        state.moving = true
        state.ghost = makeGhost(state.index)
        document.body.appendChild(state.ghost)
        // Dragging across the board's headings and hints selects them
        // otherwise, and the selection outlives the drop.
        document.body.style.userSelect = "none"
        setCarrying(state.index)
      }

      state.ghost?.style.setProperty(
        "transform",
        `translate3d(${moveEvent.clientX}px, ${moveEvent.clientY}px, 0) translate(-50%, -50%) rotate(-10deg)`
      )

      const painId = painIdAt(moveEvent.clientX, moveEvent.clientY)
      if (painId === state.over) return
      state.over = painId
      setOver(painId)
      // Recoloured to the note it is over now, not the slot it came from —
      // the whole point is that release never changes what you are looking
      // at. Off any note it keeps the colour it launched with, since it
      // cannot land there anyway.
      if (painId) {
        state.ghost?.style.setProperty(
          "--token-color",
          stickerColor(latest.current.colorIndexFor(painId))
        )
      }
    }

    const up = (upEvent: PointerEvent) => {
      const state = press.current
      if (!state || upEvent.pointerId !== state.pointerId) return
      // Read before `end` clears it, and taken from the move rather than
      // re-hit-tested here, so the vote lands on the note that was lit.
      const { moving, over: painId } = state
      end()
      if (!moving) return
      // The cancelling effect above should have taken the drag already; this
      // holds even if the release beats React to it.
      if (!latest.current.enabled) return
      if (painId) latest.current.onDrop(painId)
    }

    const cancel = (cancelEvent: PointerEvent) => {
      if (press.current && cancelEvent.pointerId !== press.current.pointerId) return
      end()
    }

    // Escape puts the sticker back, the way it closes anything else mid-gesture.
    const key = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === "Escape") end()
    }

    /*
     * On `window`, not captured on the handle, for the same reason
     * `useBoardDrag` does: a tray re-rendered mid-drag (the budget changing
     * under the room) would otherwise end the drag with it.
     */
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", cancel)
    window.addEventListener("keydown", key)
    detach.current = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", cancel)
      window.removeEventListener("keydown", key)
    }
  }

  return { carrying, over, grab }
}

/**
 * The note under the pointer, by hit test rather than cached rectangles: the
 * board can reflow, and the ghost is `pointer-events: none` so it never
 * answers this itself.
 */
function painIdAt(x: number, y: number): string | null {
  const note = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-pain-id]")
  return note?.dataset.painId ?? null
}

/**
 * The sticker being carried: a `VoteToken` in everything but implementation.
 * Built by hand and parked on `body` for the same reason `useBoardDrag`'s
 * ghost is — it moves every frame, which as React state would re-render the
 * whole board sixty times a second to move one 28px sticker.
 */
function makeGhost(index: number): HTMLElement {
  const ghost = document.createElement("div")
  ghost.className = `${styles.token} ${styles.tokenLg}`
  ghost.style.setProperty("--token-color", stickerColor(index))
  ghost.style.position = "fixed"
  ghost.style.top = "0"
  ghost.style.left = "0"
  ghost.style.zIndex = "1000"
  ghost.style.pointerEvents = "none"
  ghost.setAttribute("aria-hidden", "true")
  ghost.inert = true
  return ghost
}
